import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { 
  Users, 
  Search, 
  Filter, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  X,
  RotateCcw,
  Loader,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown
} from 'lucide-react';

const UserPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Plan limits configuration
  const planLimits = {
    Free: { brand: 0, comp: 0, gap: 0, contact: 0 },
    Growth: { brand: 1, comp: 1, gap: 1, contact: 100 },
    Pro: { brand: 2, comp: 2, gap: 2, contact: 500 },
    Enterprise: { brand: 5, comp: 5, gap: 5, contact: 'unlimited' }
  };

  const planOptions = ['Free', 'Growth', 'Pro', 'Enterprise'];
  const statusOptions = ['active', 'inactive', 'suspended'];

  const columnHelper = createColumnHelper();

  // Fetch users data
  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchUsersData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/user-panel`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });


      if (response.ok) {
        const result = await response.json();
        
        if (result.success && Array.isArray(result.data)) {
          // Ensure all user objects have required properties with defaults
          const processedUsers = result.data.map(user => ({
            id: user.id || Math.random(), // Fallback ID if missing
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            user_status: user.user_status || 'inactive',
            plan: user.plan || 'Free',
            contact_finder_count: parseInt(user.contact_finder_count) || 0,
            total_brand: parseInt(user.total_brand) || 0,
            total_comp: parseInt(user.total_comp) || 0,
            total_gap: parseInt(user.total_gap) || 0,
            email_limit: parseInt(user.email_limit) || 0,
            business_email: user.business_email || '',
            updated_at: user.updated_at || null
          }));
          
          setUsers(processedUsers);
        } else {
          console.error('Invalid data structure:', result);
          setError(result.message || 'Invalid data received from server');
        }
      } else {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        setError(`Failed to fetch users: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      setError(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updateData) => {
    try {
      setUpdating(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/user-panel/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          ...updateData
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUsers(prevUsers => 
            prevUsers.map(user => 
              user.id === userId ? { ...user, ...updateData } : user
            )
          );
          setEditingUser(null);
        } else {
          alert('Failed to update user: ' + result.message);
        }
      } else {
        alert('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Network error occurred while updating user');
    } finally {
      setUpdating(false);
    }
  };

  const resetUserCounts = async (userId, field) => {
    const resetData = { [field]: 0 };
    await updateUser(userId, resetData);
  };

  const getProgressPercentage = (current, limit) => {
    if (limit === 'unlimited' || limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const startEditing = (user) => {
    setEditingUser({
      id: user.id,
      user_status: user.user_status,
      plan: user.plan
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const saveUser = () => {
    if (editingUser) {
      updateUser(editingUser.id, {
        user_status: editingUser.user_status,
        plan: editingUser.plan
      });
    }
  };

  // Progress Bar Component
  const ProgressBar = ({ label, current, limit, userId, field }) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs text-gray-500 w-12">{label}:</span>
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${getProgressColor(getProgressPercentage(current, limit))}`}
          style={{width: `${getProgressPercentage(current, limit)}%`}}
        ></div>
      </div>
      <span className="text-xs text-gray-600 w-16">
        {current}/{limit === 'unlimited' ? '∞' : limit}
      </span>
      <button
        onClick={() => resetUserCounts(userId, field)}
        className="text-red-500 hover:text-red-700 p-1"
        title={`Reset ${label} Count`}
      >
        <RotateCcw className="w-3 h-3" />
      </button>
    </div>
  );

  // Define columns
  const columns = useMemo(() => [
    columnHelper.accessor('first_name', {
      id: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        return (
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-medium text-sm">
                {(user.first_name || 'U').charAt(0).toUpperCase()}
                {(user.last_name || '').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {user.first_name || ''} {user.last_name || ''}
              </div>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('user_status', {
      header: 'Status',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const isEditing = editingUser?.id === user.id;
        
        return isEditing ? (
          <select
            value={editingUser.user_status || 'active'}
            onChange={(e) => setEditingUser({...editingUser, user_status: e.target.value})}
            className="px-2 py-1 border rounded text-xs"
          >
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        ) : (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.user_status === 'active'
              ? 'text-green-800 bg-green-100'
              : user.user_status === 'suspended'
              ? 'text-orange-800 bg-orange-100'
              : 'text-red-800 bg-red-100'
          }`}>
            {user.user_status || 'unknown'}
          </span>
        );
      },
    }),
    columnHelper.accessor('plan', {
      header: 'Plan',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const isEditing = editingUser?.id === user.id;
        
        return isEditing ? (
          <select
            value={editingUser.plan || 'Free'}
            onChange={(e) => setEditingUser({...editingUser, plan: e.target.value})}
            className="px-2 py-1 border rounded text-xs"
          >
            {planOptions.map(plan => (
              <option key={plan} value={plan}>{plan}</option>
            ))}
          </select>
        ) : (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.plan === 'Enterprise' ? 'text-purple-800 bg-purple-100' :
            user.plan === 'Pro' ? 'text-blue-800 bg-blue-100' :
            user.plan === 'Growth' ? 'text-green-800 bg-green-100' :
            'text-gray-800 bg-gray-100'
          }`}>
            {user.plan || 'Free'}
          </span>
        );
      },
    }),
    columnHelper.accessor('total_brand', {
      id: 'usage',
      header: 'Usage Progress',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const limits = planLimits[user.plan] || planLimits.Free;
        
        return (
          <div className="space-y-1">
            <ProgressBar 
              label="Brand" 
              current={user.total_brand || 0} 
              limit={limits.brand}
              userId={user.id}
              field="total_brand"
            />
            <ProgressBar 
              label="Comp" 
              current={user.total_comp || 0} 
              limit={limits.comp}
              userId={user.id}
              field="total_comp"
            />
            <ProgressBar 
              label="Gap" 
              current={user.total_gap || 0} 
              limit={limits.gap}
              userId={user.id}
              field="total_gap"
            />
            <ProgressBar 
              label="Contact" 
              current={user.contact_finder_count || 0} 
              limit={limits.contact}
              userId={user.id}
              field="contact_finder_count"
            />
          </div>
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const isEditing = editingUser?.id === user.id;
        
        return isEditing ? (
          <div className="flex items-center gap-2">
            <button
              onClick={saveUser}
              disabled={updating}
              className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded disabled:opacity-50"
              title="Save Changes"
            >
              {updating ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
            <button
              onClick={cancelEditing}
              className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-50 rounded"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => startEditing(user)}
            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
            title="Edit User"
          >
            <Edit className="w-4 h-4" />
          </button>
        );
      },
    }),
  ], [editingUser, updating]);

  // Filter data based on status and plan filters
  const filteredData = useMemo(() => {
    if (!Array.isArray(users)) {
      return [];
    }

    let filtered = users.filter(user => 
      user && 
      typeof user === 'object' && 
      user.id !== null && 
      user.id !== undefined
    );

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.user_status === statusFilter);
    }

    if (planFilter !== 'all') {
      filtered = filtered.filter(user => user.plan === planFilter);
    }

    return filtered;
  }, [users, statusFilter, planFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen dark:bg-slate-950">
        <div className="text-center">
          <Loader className="animate-spin h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-slate-400">Loading users data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen dark:bg-slate-950">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Error Loading Users</h2>
          <p className="text-gray-600 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={fetchUsersData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-600" />
            User Panel
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mt-2">Manage user accounts, plans, and usage limits</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Active Users</p>
                <p className="text-2xl font-bold text-green-600">
                  {users.filter(u => u.user_status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Inactive Users</p>
                <p className="text-2xl font-bold text-red-600">
                  {users.filter(u => u.user_status === 'inactive').length}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Enterprise Users</p>
                <p className="text-2xl font-bold text-purple-600">
                  {users.filter(u => u.plan === 'Enterprise').length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Debug Info - Remove this after testing */}
     

        {/* Filters and Search */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Global Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={globalFilter ?? ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              {/* Plan Filter */}
              <div className="flex items-center gap-2">
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Plans</option>
                  {planOptions.map(plan => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* TanStack Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-800">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <ArrowUpDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500 dark:text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                      No users found matching your criteria
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of{' '}
                  {table.getFilteredRowModel().rows.length} results
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;